import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { NotebookPen, Plus, Loader2, Pencil, Trash2, CalendarDays, Paperclip, X, Bell, LayoutGrid, Table2 } from "lucide-react";
import { toast } from "sonner";
const EMPTY = { class_name: "", subject: "", title: "", description: "", assigned_date: new Date().toISOString().slice(0, 10), due_date: "", attachment: "", attachment_name: "" };
export default function Homework() {
  const { user } = useAuth();
  const canEdit = hasPerm(user, "manage_homework");
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(false);
  const [notifyChannel, setNotifyChannel] = useState("sms");
  const [notifyStatus, setNotifyStatus] = useState({ sms: false, whatsapp: false });
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);
  const classNames = [...new Set(classes.map((c) => (c.name || "").trim()).filter(Boolean))];
  useEffect(() => { api.get("/notify/status").then((r) => setNotifyStatus(r.data || {})).catch(() => {}); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterClass !== "all") params.class_name = filterClass;
      if (filterDate) params.date = filterDate;
      const r = await api.get("/homework", { params });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterClass, filterDate]);
  const openAdd = () => { setEditId(null); setForm({ ...EMPTY, assigned_date: filterDate || EMPTY.assigned_date }); setNotify(false); setNotifyChannel("sms"); setOpen(true); };
  const openEdit = async (h) => {
    setEditId(h.id);
    setForm({ class_name: h.class_name || "", subject: h.subject || "", title: h.title || "", description: h.description || "", assigned_date: h.assigned_date || "", due_date: h.due_date || "", attachment: "", attachment_name: h.attachment_name || "" });
    setOpen(true);
    if (h.has_attachment) {
      try { const r = await api.get(`/homework/${h.id}/attachment`); setForm((p) => ({ ...p, attachment: r.data.data, attachment_name: r.data.name })); }
      catch { /* keep name only */ }
    }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("File too large (max ~4MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, attachment: reader.result, attachment_name: f.name }));
    reader.readAsDataURL(f);
  };
  const clearFile = () => setForm((p) => ({ ...p, attachment: "", attachment_name: "" }));
  const openAttachment = async (h) => {
    try {
      const r = await api.get(`/homework/${h.id}/attachment`);
      const a = document.createElement("a");
      a.href = r.data.data; a.download = r.data.name || "attachment"; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Could not open attachment"); }
  };
  const save = async () => {
    if (!form.class_name) { toast.error("Select a class"); return; }
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/homework/${editId}`, form);
        toast.success("Homework updated");
      } else {
        const r = await api.post("/homework", { ...form, notify, notify_channel: notifyChannel });
        const ns = r.data?.notify_summary;
        if (ns) {
          if (ns.sent > 0) toast.success(`Homework assigned · ${ns.sent} parent(s) notified via ${notifyChannel === "whatsapp" ? "WhatsApp" : "SMS"}`);
          else if (ns.not_configured > 0) toast.warning("Homework assigned. SMS/WhatsApp gateway not configured — add Twilio keys to enable notifications.");
          else if (ns.no_phone === ns.total) toast.warning("Homework assigned. No students in this class have a phone number.");
          else toast.success("Homework assigned");
        } else {
          toast.success("Homework assigned");
        }
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Delete homework "${h.title}"?`)) return;
    try { await api.delete(`/homework/${h.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const isOverdue = (d) => d && d < new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-6" data-testid="homework-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Homework</h1>
          <p className="text-slate-500 text-sm mt-1">Assign and track daily homework class-wise.</p>
        </div>
        {canEdit && <Button onClick={openAdd} className="gap-1.5" data-testid="add-homework-btn"><Plus size={16} />Assign Homework</Button>}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label>Class</Label>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-48" data-testid="homework-filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-44" data-testid="homework-filter-date" />
        </div>
        {filterDate && <Button variant="outline" size="sm" onClick={() => setFilterDate("")} className="gap-1.5" data-testid="homework-filter-date-clear"><X size={14} />All Dates</Button>}
        <div className="text-xs text-slate-400 ml-auto self-center">{filterDate ? `Showing homework assigned on ${fmtDate(filterDate)}` : "Showing all dates"}</div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {items.length} {items.length === 1 ? "homework item" : "homework items"}
        </p>
        <div
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm"
          role="group"
          aria-label="Homework view"
          data-testid="homework-view-toggle"
        >
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
            aria-pressed={viewMode === "grid"}
            title="Grid view"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${viewMode === "grid" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
            data-testid="homework-grid-view"
          >
            <LayoutGrid size={16} />
            <span>Grid</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("table")}
            aria-label="Table view"
            aria-pressed={viewMode === "table"}
            title="Table view"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${viewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
            data-testid="homework-table-view"
          >
            <Table2 size={16} />
            <span>Table</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400" data-testid="homework-empty">
          <NotebookPen size={40} className="mx-auto mb-2 opacity-40" />
          No homework assigned yet.
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="homework-list">
          {items.map((h) => (
            <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-2" data-testid={`homework-card-${h.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 rounded px-2 py-0.5">{h.class_name || "—"}</span>
                    {h.subject && <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded px-2 py-0.5">{h.subject}</span>}
                  </div>
                  <h3 className="font-semibold text-slate-800 mt-2 break-words">{h.title}</h3>
                </div>
                {canEdit && (
                  <div className="flex gap-1 shrink-0">
                    <button aria-label={`Edit ${h.title}`} title="Edit homework" onClick={() => openEdit(h)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-indigo-600" data-testid={`edit-homework-${h.id}`}><Pencil size={15} /></button>
                    <button aria-label={`Delete ${h.title}`} title="Delete homework" onClick={() => remove(h)} className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`delete-homework-${h.id}`}><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              {h.description && <p className="text-sm text-slate-500 whitespace-pre-wrap break-words">{h.description}</p>}
              <div className="flex items-center gap-4 text-xs text-slate-400 mt-auto pt-2 border-t border-slate-100 flex-wrap">
                <span className="flex items-center gap-1"><CalendarDays size={13} />Assigned {h.assigned_date ? fmtDate(h.assigned_date) : "—"}</span>
                {h.due_date && <span className={`flex items-center gap-1 font-medium ${isOverdue(h.due_date) ? "text-rose-500" : "text-emerald-600"}`}>Due {fmtDate(h.due_date)}</span>}
                {h.has_attachment && <button onClick={() => openAttachment(h)} className="flex items-center gap-1 text-indigo-600 hover:underline font-medium" data-testid={`homework-attachment-${h.id}`}><Paperclip size={13} />Attachment</button>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="homework-table-wrap">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left text-sm" data-testid="homework-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">Class</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Subject</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Homework</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Assigned Date</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Due Date</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Attachment</th>
                  {canEdit && <th scope="col" className="px-4 py-3 text-right font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((h) => (
                  <tr key={h.id} className="hover:bg-slate-50/80" data-testid={`homework-row-${h.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-700">{h.class_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{h.subject || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{h.title}</div>
                      {h.description && <div className="mt-1 max-w-sm whitespace-pre-wrap break-words text-xs text-slate-500">{h.description}</div>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">{h.assigned_date ? fmtDate(h.assigned_date) : "—"}</td>
                    <td className={`whitespace-nowrap px-4 py-3 font-medium ${h.due_date ? (isOverdue(h.due_date) ? "text-rose-500" : "text-emerald-600") : "text-slate-400"}`}>{h.due_date ? fmtDate(h.due_date) : "—"}</td>
                    <td className="px-4 py-3">
                      {h.has_attachment ? (
                        <button onClick={() => openAttachment(h)} className="inline-flex items-center gap-1.5 font-medium text-indigo-600 hover:underline" data-testid={`homework-attachment-${h.id}`}><Paperclip size={14} />Open file</button>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button aria-label={`Edit ${h.title}`} title="Edit homework" onClick={() => openEdit(h)} className="rounded-lg p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600" data-testid={`edit-homework-${h.id}`}><Pencil size={15} /></button>
                          <button aria-label={`Delete ${h.title}`} title="Delete homework" onClick={() => remove(h)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`delete-homework-${h.id}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="homework-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>{editId ? "Edit Homework" : "Assign Homework"}</DialogTitle></DialogHeader>
          <div className="space-y-3 overflow-y-auto px-6 flex-1 pb-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class</Label>
                <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
                  <SelectTrigger data-testid="homework-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Mathematics" data-testid="homework-subject" /></div>
            </div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Chapter 5 exercises" data-testid="homework-title" /></div>
            <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details / instructions" data-testid="homework-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assigned Date</Label><Input type="date" value={form.assigned_date} onChange={(e) => setForm({ ...form, assigned_date: e.target.value })} data-testid="homework-assigned-date" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="homework-due-date" /></div>
            </div>
            <div>
              <Label>Attachment <span className="text-xs text-slate-400">(image or PDF, max ~4MB)</span></Label>
              {form.attachment_name ? (
                <div className="flex items-center justify-between gap-2 mt-1 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50" data-testid="homework-attachment-chip">
                  <span className="flex items-center gap-2 text-sm text-slate-700 truncate"><Paperclip size={14} className="shrink-0" />{form.attachment_name}</span>
                  <button type="button" onClick={clearFile} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid="homework-attachment-remove"><X size={16} /></button>
                </div>
              ) : (
                <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-500 cursor-pointer hover:border-indigo-400 hover:text-indigo-600 transition-colors" data-testid="homework-attachment-label">
                  <Paperclip size={15} />Choose file
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="homework-attachment-input" />
                </label>
              )}
            </div>
            {!editId && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/60">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                  <input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="homework-notify-toggle" />
                  <Bell size={15} className="text-indigo-600" />Notify parents when assigned
                </label>
                {notify && (
                  <div className="flex items-center gap-3 pl-6">
                    <Label className="text-xs text-slate-500">Channel</Label>
                    <Select value={notifyChannel} onValueChange={setNotifyChannel}>
                      <SelectTrigger className="w-40 h-9" data-testid="homework-notify-channel"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {notify && !notifyStatus[notifyChannel] && (
                  <p className="text-[11px] text-amber-600 pl-6" data-testid="homework-notify-warning">Gateway not configured (MOCKED). Add Twilio keys in backend to actually send.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0"><Button onClick={save} disabled={saving} data-testid="save-homework-btn">{saving ? <Loader2 className="animate-spin" /> : (editId ? "Save Changes" : "Assign Homework")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
